import { EditablePreviewWorld } from '../../types'
import { playSample } from '../audio'
import { TIMER_TICK_SECONDS } from '../constants'
import { GOAL_SUCCESS_SFX, JUMP_SFX } from '../missionSfx'
import { showHudText } from '../sharedHelpers'
import { BehaviorContext, BehaviorHandler, HudStyle } from '../types'

/**
 * スタート/ゴール式レースタイマー（Lua: StarterGui/LocalScript + StartScript +
 * GoalScript）。rjf07/rjf08 に同型のタイマーが 2 回重複したため抽出する
 * （rjf08 に「3 つ目の同型が現れた時点で抽出する」と記録していた判断を、
 * 本リファクタで前倒しに実行したもの）。
 *
 * - Start 接触でタイマー開始（-1 始まりで毎秒 +1、最初の表示は 0）+ スタート音。
 *   進行中の再接触では二重起動しない（isStart ガード）。接触のたびに
 *   ゴールの touched をリセットする（ゴール後の再走を可能にする）。
 * - ゴール接触でタイマー停止 + ゴール音。接触のたびに applyGoalEffect で
 *   Humanoid 操作（速度・ジャンプ力）を適用する。
 *
 * スタート音・ゴール音は実音源（rjf07/08 とも同じ JUMP_SFX / GOAL_SUCCESS_SFX）。
 */

export interface RaceTimerConfig {
  /** タイマー GUI の実機デザイン（各ミッションの TextLabel 実測値）。 */
  readonly hudStyle: HudStyle
  /**
   * ゴール接触のたびに適用する効果（Lua: GoalScript の Humanoid 操作）。
   * rjf07 は強化（WalkSpeed=32 → JumpPower=100）、rjf08 は標準への
   * リセット（JumpPower=50 → WalkSpeed=16）。適用順も元 Lua のまま渡す。
   */
  readonly applyGoalEffect: (world: EditablePreviewWorld) => void
}

/** レースタイマーが提供するハンドラ群。ミッション側で名前に結び付ける。 */
export interface RaceTimerHandlers {
  /**
   * 実機の TextLabel は初期 Text=0 がプレイ開始時から出ている
   * （カウントは Start 接触まで始まらない）。onPlay に登録する。
   */
  readonly showInitialTimer: BehaviorHandler
  readonly onStartTouched: BehaviorHandler
  readonly onGoalTouched: BehaviorHandler
}

/**
 * レースタイマーを生成する。状態（isStart / goalTouched / 世代トークン）は
 * closure に持ち、ミッションの factory がプレイ毎に作り直すことでリセットする。
 */
export const createRaceTimer = (config: RaceTimerConfig): RaceTimerHandlers => {
  // Lua の LocalScript（isStart）と GoalScript（touched）の状態。
  let isStart = false
  let goalTouched = false
  // タイマーループの世代トークン。「現役は最新世代の 1 本だけ」を保証する。
  // 共有フラグ（isGoal）を終了条件にすると、ゴール直後の Start 再接触で
  // 旧ループの待機中にフラグが裏返り、新旧 2 本が同時カウントする競合が
  // あるため、起動時の世代と一致する間だけ回す。
  let timerRunToken = 0

  /** タイマーループ（Lua: startTimer）。世代が現役の間、毎秒 +1 を表示する。 */
  const runTimer = async (
    ctx: BehaviorContext,
    token: number,
  ): Promise<void> => {
    let timer = -1
    while (timerRunToken === token && !ctx.signal.aborted) {
      timer += 1
      ctx.setHudText?.(String(timer), config.hudStyle)
      await ctx.wait(TIMER_TICK_SECONDS)
    }
  }

  const onStartTouched: BehaviorHandler = (ctx) => {
    // Lua: StartScript は接触のたびにゴールの touched をリセットする（再走可能）。
    goalTouched = false
    if (isStart) {
      return
    }
    isStart = true
    // Lua: StarterGui/LocalScript の StartEvent でスタート音を鳴らす。
    playSample(JUMP_SFX.url, JUMP_SFX.volume, ctx.signal)
    timerRunToken += 1
    return runTimer(ctx, timerRunToken)
  }

  const onGoalTouched: BehaviorHandler = (ctx) => {
    // Lua: GoalScript は接触のたびに Humanoid 操作を適用する。
    config.applyGoalEffect(ctx.world)
    if (goalTouched) {
      return
    }
    goalTouched = true
    // 世代を進めて現役タイマーを失効させる + ゴール音。
    isStart = false
    timerRunToken += 1
    playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
  }

  const showInitialTimer: BehaviorHandler = showHudText('0', config.hudStyle)

  return { showInitialTimer, onStartTouched, onGoalTouched }
}
